import React from "react";
import { format } from "date-fns";
import { DocumentDownloadIcon } from "@heroicons/react/outline";

const LedgerView = ({ plan, ledger }) => {
  console.log({ ledger, plan });
  const getEntryTypeColor = (type) => {
    switch (type) {
      case "PAYMENT":
        return "text-success-dark bg-success-light";
      case "BENEFIT":
        return "text-white bg-primary-light";
      case "REDEMPTION":
        return "text-secondary-dark bg-secondary-light";
      case "ADJUSTMENT":
        return "text-warning-dark bg-warning-light";
      case "REVERSAL":
        return "text-danger-dark bg-danger-light";
      default:
        return "text-text bg-neutral-light";
    }
  };

  const getEntryIcon = (type) => {
    switch (type) {
      case "PAYMENT":
        return "↓";
      case "BENEFIT":
        return "🎁";
      case "REDEMPTION":
        return "↑";
      case "ADJUSTMENT":
        return "⚖️";
      case "REVERSAL":
        return "↩️";
      default:
        return "•";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "VERIFIED":
        return "text-success";
      case "PENDING_REVIEW":
        return "text-warning";
      case "REJECTED":
        return "text-danger";
      default:
        return "text-neutral-dark";
    }
  };

  // Convert payment records to ledger entries format
  const formatLedgerEntries = () => {
    if (!ledger?.length) {
      return [
        {
          _id: "1",
          entryNumber: "L001",
          entryType: "ADJUSTMENT",
          amountInr: 0,
          description: "Plan created",
          balanceAfter: { valueInr: 0, grams: 0 },
          createdAt: plan.createdAt,
          createdBy: { name: "System" },
          status: "VERIFIED",
        },
      ];
    }

    // Sort payments by date (newest first)
    const sortedPayments = [...ledger].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    return sortedPayments.map((payment) => ({
      _id: payment._id,
      entryNumber: payment.paymentNumber || "Unknown",
      entryType: "PAYMENT",
      amountInr: payment.verifiedAmount || payment.amountCollected || 0,
      description: `Payment via ${payment.method || "Unknown"} ${
        payment.allocations?.length > 0
          ? `- Installment #${payment.allocations[0].sequenceNumber}`
          : ""
      }`,
      balanceAfter: {
        valueInr: plan.totalVerifiedAmount || 0,
        grams: plan.wallet?.grams || 0,
      },
      createdAt: payment.createdAt,
      createdBy: {
        name: payment.verifiedBy
          ? "Admin"
          : payment.enteredBy
          ? "Staff"
          : "System",
      },
      status: payment.status || "PENDING_REVIEW",
    }));
  };

  const entries = formatLedgerEntries();

  const handleExport = (format) => {
    console.log(`Exporting ledger in ${format} format`);
  };

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-text">Ledger Entries</h3>
          <p className="text-sm text-neutral-dark mt-1">
            Complete transaction history for this plan
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleExport("csv")}
            className="inline-flex items-center px-3 py-2 border border-neutral-light bg-background text-sm font-medium text-text rounded-md hover:bg-neutral-light"
          >
            <DocumentDownloadIcon className="h-4 w-4 mr-2" />
            CSV
          </button>
          <button
            onClick={() => handleExport("excel")}
            className="inline-flex items-center px-3 py-2 border border-neutral-light bg-background text-sm font-medium text-text rounded-md hover:bg-neutral-light"
          >
            <DocumentDownloadIcon className="h-4 w-4 mr-2" />
            Excel
          </button>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-background border border-neutral-light rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-neutral-light">
          <thead className="bg-neutral-light">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Entry #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Date & Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                By
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-neutral-light">
            {entries.map((entry) => (
              <tr key={entry._id} className="hover:bg-neutral-light">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text">
                  {entry.entryNumber}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-dark">
                  <div>
                    <p>{format(new Date(entry.createdAt), "dd MMM yyyy")}</p>
                    <p className="text-xs">
                      {format(new Date(entry.createdAt), "HH:mm:ss")}
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEntryTypeColor(
                      entry.entryType
                    )}`}
                  >
                    <span className="mr-1">
                      {getEntryIcon(entry.entryType)}
                    </span>
                    {entry.entryType}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-text">
                  {entry.description}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                  {entry.entryType === "PAYMENT" && (
                    <>
                      {entry.status === "VERIFIED" && (
                        <span className="text-success font-medium">
                          +₹{entry.amountInr?.toLocaleString()}
                        </span>
                      )}
                      {entry.status === "PENDING_REVIEW" && (
                        <span className="text-warning font-medium">
                          (+₹{entry.amountInr?.toLocaleString()})
                        </span>
                      )}
                      {entry.status === "REJECTED" && (
                        <span className="text-danger font-medium line-through">
                          +₹{entry.amountInr?.toLocaleString()}
                        </span>
                      )}
                    </>
                  )}
                  {entry.entryType === "REDEMPTION" && (
                    <span className="text-danger font-medium">
                      -₹{entry.amountInr?.toLocaleString()}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {entry.entryType === "PAYMENT" && (
                    <span className={getStatusColor(entry.status)}>
                      {entry.status === "VERIFIED" && "Verified"}
                      {entry.status === "PENDING_REVIEW" && "Pending Review"}
                      {entry.status === "REJECTED" && "Rejected"}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-dark">
                  {entry.createdBy?.name || "System"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LedgerView;
